import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../AuthContext';
import { getEffectiveAccountId, getCurrentUser } from '../services/authorizationService';
import { Warning } from '@phosphor-icons/react';

const SubscriptionGuard = ({ children, fallback = null }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { 
    needsPaymentUpdate, 
    isInGracePeriod, 
    getGracePeriodDaysRemaining,
    loading 
  } = useSubscription();

  // Get the effective account ID (client account when admin is scoped)
  const effectiveAccountId = getEffectiveAccountId();
  
  // Get the current user with role from authorization service
  const userWithRole = getCurrentUser();

  // Debug logging
  console.log('SubscriptionGuard render:', {
    currentUser: currentUser?.email,
    role: currentUser?.role,
    userWithRole: userWithRole?.role,
    loading,
    effectiveAccountId
  });

  // Show loading state
  if (loading) {
    console.log('SubscriptionGuard: Still loading...');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Admins always have access, regardless of whether they're viewing a client account
  // Check both currentUser.role and userWithRole.role to handle timing issues
  // Also handle both uppercase and lowercase role values
  const isAdmin = currentUser?.role === 'ADMIN' || currentUser?.role === 'admin' ||
                  userWithRole?.role === 'ADMIN' || userWithRole?.role === 'admin';
  
  if (isAdmin) {
    console.log('Admin user detected, allowing access:', currentUser?.email || userWithRole?.email, 'Effective account:', effectiveAccountId);
    return children;
  }

  console.log('Not an admin, checking subscription access...');

  // For non-admin users, allow dashboard access but with limited navigation
  // All clients (with or without subscription) should see the dashboard
  // The DashboardLayout will handle showing appropriate navigation items

  // Show grace period warning if payment failed
  if (needsPaymentUpdate() && isInGracePeriod()) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Grace Period Banner */}
        <div className="bg-yellow-50 border-b border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Warning className="w-5 h-5 text-yellow-600 mr-2" />
                  <p className="text-sm text-yellow-800">
                    <strong>Payment Failed:</strong> You have {getGracePeriodDaysRemaining()} days to update your payment method.
                  </p>
                </div>
                <button
                  onClick={() => navigate('/dashboard/settings')}
                  className="text-sm bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700 transition-colors"
                >
                  Update Payment
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Main Content */}
        {children}
      </div>
    );
  }

  // All clients (with or without subscription) should have dashboard access
  // The DashboardLayout will handle showing appropriate navigation items

  // User has access, render children
  return children;
};

export default SubscriptionGuard;
